import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import './GeneralAudioManager.css';

function GeneralAudioManager() {
  const [audioFiles, setAudioFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [audioPremium, setAudioPremium] = useState(false);

  useEffect(() => {
    loadAudioFiles();
  }, []);

  const loadAudioFiles = async () => {
    try {
      const data = await apiClient.getGeneralAudio();
      setAudioFiles(data.files || []);
    } catch (error) {
      console.error('Error loading general audio:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/webm'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|webm)$/i)) {
      setError('Bitte wählen Sie eine gültige Audio-Datei (MP3, WAV, OGG, WebM)');
      return;
    }

    setUploading(true);
    setError('');

    try {
      await apiClient.uploadGeneralAudio(file, audioPremium);
      await loadAudioFiles();
      e.target.value = '';
      setAudioPremium(false); // Reset nach Upload
    } catch (err) {
      setError(err.message || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm('Möchten Sie diese Audio-Datei wirklich löschen?')) {
      return;
    }

    try {
      await apiClient.deleteGeneralAudio(filename);
      await loadAudioFiles();
    } catch (error) {
      console.error('Error deleting audio:', error);
      alert('Fehler beim Löschen');
    }
  };

  return (
    <div className="general-audio-manager">
      <div className="audio-manager-header">
        <div>
          <h3 className="audio-manager-title">📚 Allgemeine Audio-Bibliothek</h3>
          <p className="audio-manager-hint">
            Diese Audio-Dateien werden für alle Frequenzen verwendet, die keine eigenen Dateien haben
          </p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="audio-upload-section">
        <div className="upload-controls">
          <label className="premium-toggle">
            <input
              type="checkbox"
              checked={audioPremium}
              onChange={(e) => setAudioPremium(e.target.checked)}
            />
            <span>💎 Nur für Premium-User</span>
          </label>
        </div>
        
        <label htmlFor="general-audio-upload" className="upload-label">
          <span className="upload-icon">⬆️</span>
          <span>{uploading ? 'Hochladen...' : `Audio-Datei hochladen ${audioPremium ? '(Premium)' : '(Standard)'}`}</span>
        </label>
        <input
          id="general-audio-upload"
          type="file"
          accept="audio/mp3,audio/mpeg,audio/wav,audio/ogg,audio/webm"
          onChange={handleUpload}
          disabled={uploading}
          style={{ display: 'none' }}
        />
      </div>

      {audioFiles.length === 0 ? (
        <div className="empty-audio-state">
          <p>📁 Noch keine allgemeinen Audio-Dateien vorhanden</p>
          <p className="empty-hint">Lade Dateien hoch, die als Fallback für alle Frequenzen dienen</p>
        </div>
      ) : (
        <div className="audio-files-list">
          {audioFiles.map((fileObj, index) => (
            <div key={fileObj.filename} className="audio-file-item">
              <span className="file-number">{index + 1}.</span>
              <span className="file-icon">🎵</span>
              <span className="file-name">{fileObj.filename}</span>
              {fileObj.is_premium && (
                <span className="premium-badge">💎 Premium</span>
              )}
              <button
                onClick={() => handleDelete(fileObj.filename)}
                className="btn-delete-audio-file"
                title="Löschen"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="audio-info-box">
        <div className="info-icon">💡</div>
        <div className="info-text">
          <strong>Wie es funktioniert:</strong> Wenn eine Frequenz keine eigenen Audio-Dateien hat, 
          werden automatisch zufällige Dateien aus dieser allgemeinen Bibliothek abgespielt.
          Premium-Audios sind nur für Premium-User verfügbar.
        </div>
      </div>
    </div>
  );
}

export default GeneralAudioManager;
